import React, { useEffect, useState } from "react";
import { supabase } from "./supabase";

interface Player {
  id: string | number;
  name: string;
  position: string;
  hometown: string;
  rank: number;
  session_id: string;
}

export const PlayerPool = ({
  isAdmin,
  sessionId,
  canDraft, // Added to resolve the error
}: {
  isAdmin: boolean;
  sessionId?: string;
  canDraft?: boolean; // Defined as an optional boolean
}) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [totalPicks, setTotalPicks] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [session, setSession] = useState<any>(null);

  // States for Edit/Delete Modal
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = async () => {
    if (!sessionId) return;
    const [sessRes, picksRes, playersRes, teamsRes] = await Promise.all([
      supabase.from("draft_sessions").select("*").eq("id", sessionId).single(),
      supabase.from("picks").select("player_id").eq("session_id", sessionId),
      supabase
        .from("players")
        .select("*")
        .eq("session_id", sessionId)
        .order("rank", { ascending: true }),
      supabase
        .from("teams")
        .select("*")
        .order("draft_position", { ascending: true }),
    ]);

    setSession(sessRes.data);
    const draftedIds = (picksRes.data || []).map((p: any) => p.player_id);
    const available = (playersRes.data || []).filter(
      (p: any) => draftedIds.indexOf(p.id) === -1
    );
    setPlayers(available);
    setTeams(teamsRes.data || []);
    setTotalPicks(draftedIds.length);
  };

  useEffect(() => {
    fetchData();

    const channel = supabase
      .channel(`pool-sync-${sessionId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "picks" },
        () => fetchData()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "draft_sessions" },
        () => fetchData()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "players" },
        () => fetchData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const handleUpdatePlayer = async () => {
    if (!editingPlayer) return;
    setIsSaving(true);
    const { error } = await supabase
      .from("players")
      .update({
        name: editingPlayer.name,
        position: editingPlayer.position,
        hometown: editingPlayer.hometown,
      })
      .eq("id", editingPlayer.id);
    if (error) alert("Update failed: " + error.message);
    setIsSaving(false);
    setEditingPlayer(null);
  };

  const handleDeletePlayer = async (id: string | number) => {
    if (!window.confirm("DELETE THIS PLAYER?")) return;
    const { error } = await supabase.from("players").delete().eq("id", id);
    if (error) alert("Delete failed: " + error.message);
    setEditingPlayer(null);
  };

  const handleDraftPlayer = async (player: Player) => {
    // Logic updated to respect the canDraft prop if needed,
    // though the current implementation relies on session state
    if (!session || !session.is_active || session.is_paused) return;
    const numTeams = teams.length;
    if (numTeams === 0) return;

    const currentPickNum = totalPicks + 1;
    const roundNumber = Math.ceil(currentPickNum / numTeams);
    const roundPickIndex = (currentPickNum - 1) % numTeams;

    let pickingTeam;
    if (roundNumber % 2 !== 0) {
      pickingTeam = teams[roundPickIndex];
    } else {
      pickingTeam = teams[numTeams - 1 - roundPickIndex];
    }

    if (!pickingTeam) return;

    const { error } = await supabase.from("picks").insert([
      {
        player_id: player.id,
        team_id: pickingTeam.id,
        session_id: sessionId,
        pick_number: currentPickNum,
        round_number: roundNumber,
      },
    ]);
    if (error) {
      alert("Error drafting player: " + error.message);
    }
  };

  const filteredPlayers = players.filter(
    (p) => p.name.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1
  );

  return (
    <div style={{ marginTop: "40px", fontFamily: "'Oswald', sans-serif" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          borderBottom: "2px solid #22565a",
          paddingBottom: "10px",
        }}
      >
        <h2 style={{ color: "#f9eeda", margin: 0, textTransform: "uppercase" }}>
          AVAILABLE PLAYERS
        </h2>
        <input
          placeholder="SEARCH PLAYERS..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            background: "#111",
            color: "#f9eeda",
            border: "1px solid #22565a",
            padding: "8px 15px",
            borderRadius: "4px",
            fontFamily: "'Oswald', sans-serif",
            width: "250px",
          }}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: "15px",
        }}
      >
        {filteredPlayers.map((p) => (
          <div
            key={p.id}
            style={{
              background: "#080808",
              border: "1px solid #222",
              padding: "15px 10px",
              textAlign: "center",
              borderRadius: "4px",
              position: "relative",
            }}
          >
            {isAdmin && (
              <button
                onClick={() => setEditingPlayer(p)}
                style={{
                  position: "absolute",
                  top: "5px",
                  right: "5px",
                  background: "transparent",
                  border: "none",
                  color: "#22565a",
                  cursor: "pointer",
                  fontSize: "1rem",
                }}
              >
                ✎
              </button>
            )}
            <div
              style={{
                fontSize: "0.75rem",
                color: "#22565a",
                textTransform: "uppercase",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {p.hometown || "UNKNOWN"}
            </div>
            <div
              style={{
                fontSize: "1.25rem",
                color: "#f9eeda",
                textTransform: "uppercase",
                margin: "5px 0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
              }}
              title={p.name}
            >
              {p.name}
            </div>
            <div
              style={{
                color: "#666",
                marginBottom: "12px",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {p.position}
            </div>
            {isAdmin && (
              <button
                onClick={() => handleDraftPlayer(p)}
                disabled={!session?.is_active || session?.is_paused}
                style={{
                  width: "100%",
                  padding: "10px 5px",
                  background:
                    !session?.is_active || session?.is_paused
                      ? "#333"
                      : "#22565a",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: "bold",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  fontSize: "0.85rem",
                }}
              >
                Draft Player
              </button>
            )}
          </div>
        ))}
      </div>

      {editingPlayer && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#151513",
              border: "2px solid #22565a",
              padding: "30px",
              borderRadius: "8px",
              width: "350px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <h3 style={{ color: "#f9eeda", margin: 0, textAlign: "center" }}>
              EDIT PLAYER
            </h3>
            <input
              style={{
                background: "#080808",
                color: "#f9eeda",
                border: "1px solid #22565a",
                padding: "10px",
                borderRadius: "4px",
              }}
              value={editingPlayer.name}
              onChange={(e) =>
                setEditingPlayer({ ...editingPlayer, name: e.target.value })
              }
              placeholder="NAME"
            />
            <input
              style={{
                background: "#080808",
                color: "#f9eeda",
                border: "1px solid #22565a",
                padding: "10px",
                borderRadius: "4px",
              }}
              value={editingPlayer.position}
              onChange={(e) =>
                setEditingPlayer({ ...editingPlayer, position: e.target.value })
              }
              placeholder="POSITION"
            />
            <input
              style={{
                background: "#080808",
                color: "#f9eeda",
                border: "1px solid #22565a",
                padding: "10px",
                borderRadius: "4px",
              }}
              value={editingPlayer.hometown}
              onChange={(e) =>
                setEditingPlayer({ ...editingPlayer, hometown: e.target.value })
              }
              placeholder="HOMETOWN"
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button
                onClick={handleUpdatePlayer}
                disabled={isSaving}
                style={{
                  flex: 1,
                  background: "#22565a",
                  color: "white",
                  padding: "10px",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                {isSaving ? "..." : "UPDATE"}
              </button>
              <button
                onClick={() => handleDeletePlayer(editingPlayer.id)}
                style={{
                  flex: 1,
                  background: "#4a1212",
                  color: "white",
                  padding: "10px",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                DELETE
              </button>
            </div>
            <button
              onClick={() => setEditingPlayer(null)}
              style={{
                background: "transparent",
                color: "#666",
                border: "none",
                marginTop: "10px",
                cursor: "pointer",
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
